import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	Chip,
	Stack,
	IconButton,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/TagSummaryFilterSection';
import { useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import SetOrderDialog from './components/SetOrderDialog';
import {
	useDeleteUnitTagListMutation,
	useGetUnitTagListQuery,
} from '@/api/exercise/unitTagApiSlice';
import {
	UnitTagFilter,
	UnitTagListDTO,
} from '@/api/DTO/exercise/unitTag.interface';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useDeleteNoticeMutation } from '@/api/app-setting/noticeApiSlice';
import AddPageDialog from './components/AddTagDialog';
import { FormType } from '@/api/enum/formType.enum';

export interface ListDataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function TagListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const showModal = useModal();
	const [deleteUnitTagList] = useDeleteUnitTagListMutation();

	const defaultPropertyFilter: UnitTagFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<UnitTagFilter> = defaultPropertyFilter;
	const methods = useForm<UnitTagFilter>({
		defaultValues,
	});
	const transformedSearchParams: UnitTagFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetUnitTagListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: UnitTagListDTO[]) => {
		if (response == null) return;
		const newResponse: ListDataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as UnitTagFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const handleDelete = async (obj: any) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('exerciseSetting.deleteTag')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('exerciseSetting.isDeleteTag')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteUnitTagList(obj.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteTagSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	const columns: GridColDef[] = [
		{
			field: 'nameZh',
			headerName: t('exerciseSetting.tagTextZh'),
			flex: 21,
		},
		{
			field: 'nameEn',
			headerName: t('exerciseSetting.tagTextEn'),
			flex: 21,
		},

		{
			field: 'updatedAt',
			headerName: t('exerciseSetting.lastUpdateTime'),
			flex: 15,
		},
		{
			field: 'createdAt',
			headerName: t('exerciseSetting.createTime'),
			flex: 15,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<AddPageDialog type={FormType.edit} row={params.row} />
			),
		},
		{
			field: 'delete',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<IconButton color='error' onClick={() => handleDelete(params.row)}>
					<DeleteOutlinedIcon />
				</IconButton>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.unitTag')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<AddPageDialog type={FormType.create} />
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
